import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Bell, Check, CheckCheck, Trash2, Settings, Loader2,
    FlaskConical, CloudSun, ShoppingCart, Tag, ChevronRight,
    BellOff, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const NOTIFICATION_ICONS = {
    analysis_reminder: FlaskConical,
    weather_alert: CloudSun,
    order_update: ShoppingCart,
    promo: Tag,
    default: Bell
};

const NOTIFICATION_COLORS = {
    analysis_reminder: 'text-blue-500 bg-blue-100',
    weather_alert: 'text-amber-500 bg-amber-100',
    order_update: 'text-green-500 bg-green-100',
    promo: 'text-purple-500 bg-purple-100',
    default: 'text-gray-500 bg-gray-100'
};

function NotificationItem({ notification, onRead, onDelete }) {
    const Icon = NOTIFICATION_ICONS[notification.type] || NOTIFICATION_ICONS.default;
    const colorClass = NOTIFICATION_COLORS[notification.type] || NOTIFICATION_COLORS.default;
    
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diff = now - date;
            
            if (diff < 60000) return "À l'instant";
            if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
            if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
            
            return date.toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    return (
        <div 
            className={`flex items-start gap-4 p-4 rounded-lg transition-colors ${
                notification.read ? 'bg-muted/30' : 'bg-primary/5 border border-primary/20'
            }`}
            data-testid={`notification-${notification.notification_id}`}
        >
            <div className={`p-2 rounded-full ${colorClass}`}>
                <Icon className="h-5 w-5" />
            </div>
            
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <h4 className={`font-medium ${!notification.read && 'font-semibold'}`}>
                            {notification.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1">
                            {notification.message}
                        </p>
                    </div>
                    {!notification.read && (
                        <Badge className="shrink-0 bg-primary">Nouveau</Badge>
                    )}
                </div>
                
                <div className="flex items-center gap-4 mt-3">
                    <span className="text-xs text-muted-foreground">
                        {formatDate(notification.created_at)}
                    </span>
                    
                    {notification.link && (
                        <Link 
                            to={notification.link}
                            className="text-xs text-primary font-medium flex items-center gap-1 hover:underline"
                        >
                            Voir <ChevronRight className="h-3 w-3" />
                        </Link>
                    )}
                    
                    <div className="flex items-center gap-2 ml-auto">
                        {!notification.read && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onRead(notification.notification_id)}
                                className="h-7 text-xs"
                            >
                                <Check className="h-3 w-3 mr-1" />
                                Marquer lu
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDelete(notification.notification_id)}
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function NotificationPreferencesForm({ preferences, onSave }) {
    const [prefs, setPrefs] = useState(preferences);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiRequest('/notifications/preferences', {
                method: 'POST',
                body: JSON.stringify(prefs)
            });
            toast.success('Préférences mises à jour');
            onSave(prefs);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const days = [
        { value: 'monday', label: 'Lundi' },
        { value: 'tuesday', label: 'Mardi' },
        { value: 'wednesday', label: 'Mercredi' },
        { value: 'thursday', label: 'Jeudi' },
        { value: 'friday', label: 'Vendredi' },
        { value: 'saturday', label: 'Samedi' },
        { value: 'sunday', label: 'Dimanche' }
    ];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Préférences de notification
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label htmlFor="analysis-reminder">Rappel d'analyse</Label>
                            <p className="text-sm text-muted-foreground">
                                Rappel hebdomadaire pour analyser l'eau
                            </p>
                        </div>
                        <Switch
                            id="analysis-reminder"
                            checked={prefs.analysis_reminder}
                            onCheckedChange={(v) => setPrefs({...prefs, analysis_reminder: v})}
                        />
                    </div>

                    {prefs.analysis_reminder && (
                        <div className="ml-4 pl-4 border-l">
                            <Label>Jour du rappel</Label>
                            <Select
                                value={prefs.reminder_day}
                                onValueChange={(v) => setPrefs({...prefs, reminder_day: v})}
                            >
                                <SelectTrigger className="w-48 mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {days.map(day => (
                                        <SelectItem key={day.value} value={day.value}>
                                            {day.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        <div>
                            <Label htmlFor="weather-alerts">Alertes météo</Label>
                            <p className="text-sm text-muted-foreground">
                                Alertes orages, canicule et UV
                            </p>
                        </div>
                        <Switch
                            id="weather-alerts"
                            checked={prefs.weather_alerts}
                            onCheckedChange={(v) => setPrefs({...prefs, weather_alerts: v})}
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <Label htmlFor="order-updates">Suivi de commande</Label>
                            <p className="text-sm text-muted-foreground">
                                Mises à jour de vos commandes
                            </p>
                        </div>
                        <Switch
                            id="order-updates"
                            checked={prefs.order_updates}
                            onCheckedChange={(v) => setPrefs({...prefs, order_updates: v})}
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <Label htmlFor="promotions">Promotions</Label>
                            <p className="text-sm text-muted-foreground">
                                Offres spéciales et réductions
                            </p>
                        </div>
                        <Switch
                            id="promotions"
                            checked={prefs.promotions}
                            onCheckedChange={(v) => setPrefs({...prefs, promotions: v})}
                        />
                    </div>
                </div>

                <Button onClick={handleSave} disabled={saving} className="w-full">
                    {saving ? (
                        <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Enregistrement...
                        </>
                    ) : (
                        'Enregistrer les préférences'
                    )}
                </Button>
            </CardContent>
        </Card>
    );
}

export default function NotificationsPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [preferences, setPreferences] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        if (user) {
            loadNotifications();
            loadPreferences();
        }
    }, [user]);

    const loadNotifications = async () => {
        try {
            const data = await apiRequest('/notifications');
            setNotifications(data.notifications || []);
            setUnreadCount(data.unread_count || 0);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPreferences = async () => {
        try {
            const data = await apiRequest('/notifications/preferences');
            setPreferences(data);
        } catch (error) {
            console.error('Error loading preferences:', error);
        }
    };

    const handleMarkRead = async (notificationId) => {
        try {
            await apiRequest(`/notifications/${notificationId}/read`, { method: 'POST' });
            setNotifications(notifications.map(n => 
                n.notification_id === notificationId ? {...n, read: true} : n
            ));
            setUnreadCount(Math.max(0, unreadCount - 1));
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await apiRequest('/notifications/read-all', { method: 'POST' });
            setNotifications(notifications.map(n => ({...n, read: true})));
            setUnreadCount(0);
            toast.success('Toutes les notifications marquées comme lues');
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            await apiRequest(`/notifications/${notificationId}`, { method: 'DELETE' });
            const notification = notifications.find(n => n.notification_id === notificationId);
            setNotifications(notifications.filter(n => n.notification_id !== notificationId));
            if (!notification?.read) {
                setUnreadCount(Math.max(0, unreadCount - 1));
            }
            toast.success('Notification supprimée');
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <Bell className="h-16 w-16 mx-auto text-primary mb-4" />
                    <h1 className="text-3xl font-bold mb-4">Notifications</h1>
                    <p className="text-muted-foreground mb-6">
                        Connectez-vous pour voir vos notifications
                    </p>
                    <Button onClick={() => navigate('/auth')}>
                        Se connecter
                    </Button>
                </div>
            </div>
        );
    }

    const filteredNotifications = activeTab === 'unread' 
        ? notifications.filter(n => !n.read)
        : notifications;

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-8 pt-24" data-testid="notifications-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <Bell className="h-8 w-8 text-primary" />
                        <div>
                            <h1 className="text-3xl font-bold">Notifications</h1>
                            {unreadCount > 0 && (
                                <p className="text-sm text-muted-foreground">
                                    {unreadCount} non lue(s)
                                </p>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={loadNotifications}
                            className="rounded-full"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                        {unreadCount > 0 && (
                            <Button
                                variant="outline"
                                onClick={handleMarkAllRead}
                            >
                                <CheckCheck className="h-4 w-4 mr-2" />
                                Tout marquer lu
                            </Button>
                        )}
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="all">
                            Toutes ({notifications.length})
                        </TabsTrigger>
                        <TabsTrigger value="unread">
                            Non lues ({unreadCount})
                        </TabsTrigger>
                        <TabsTrigger value="settings">
                            Paramètres
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="all" className="space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        ) : filteredNotifications.length === 0 ? (
                            <Card className="bg-muted/50">
                                <CardContent className="py-12 text-center">
                                    <BellOff className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">
                                        Aucune notification
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            filteredNotifications.map(notification => (
                                <NotificationItem
                                    key={notification.notification_id}
                                    notification={notification}
                                    onRead={handleMarkRead}
                                    onDelete={handleDelete}
                                />
                            ))
                        )}
                    </TabsContent>

                    <TabsContent value="unread" className="space-y-4">
                        {filteredNotifications.length === 0 ? (
                            <Card className="bg-muted/50">
                                <CardContent className="py-12 text-center">
                                    <CheckCheck className="h-12 w-12 mx-auto text-green-500 mb-4" />
                                    <p className="text-muted-foreground">
                                        Toutes vos notifications ont été lues !
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            filteredNotifications.map(notification => (
                                <NotificationItem
                                    key={notification.notification_id}
                                    notification={notification}
                                    onRead={handleMarkRead}
                                    onDelete={handleDelete}
                                />
                            ))
                        )}
                    </TabsContent>

                    <TabsContent value="settings">
                        {preferences && (
                            <NotificationPreferencesForm
                                preferences={preferences}
                                onSave={setPreferences}
                            />
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
